import { YES_FLAG, NO_FLAG } from '../shared/constants';

import fetch from '../utils/fetchService';
import Config from '../config';
import {
  handleErrors,
  parseAsJson,
} from '../utils/requestHelpers';
import { ALL_ASSIGNMENTS_CODE } from './constants';
import { YesNoFlag } from '../shared/types';

const mapPrivileges = privileges => !privileges ? [] : privileges.map(privilege => ({ privilegeCode: privilege }));

const mapUserForApi = user => ({
  sso: user.sso,
  firstName: user.firstName,
  lastName: user.lastName,
  email: user.email,
  activeFlag: user.activeFlag ? YES_FLAG : NO_FLAG,
  clients: user.checkedClients.map(client => {
    const userPermissions = user.permissions.find(permissions => permissions.clientId === client.clientId);
    const { roleId, name } = user.role.find(role => role.clientId === client.clientId);
    const userExpirationDate = user.expirationDate.find(expirationDate => expirationDate.clientId === client.clientId);
    return ({
      clientId: client.clientId,
      name: client.name,
      roleId,
      roleName: name,
      assignments: [
        ...user.checkedAssignments
          .filter(assignment => assignment.clientId === client.clientId)
          .map(assignment => ({
            assignmentType: assignment.assignmentType,
            assignmentCode: assignment.assignmentCode === ALL_ASSIGNMENTS_CODE ? 'ALL' : assignment.assignmentCode,
            privileges: mapPrivileges(assignment.privileges),
          })),
        {
          assignmentType: 'USR',
          assignmentCode: user.sso,
          privileges: mapPrivileges(
            userPermissions ? userPermissions.permissions : null
          ),
        },
      ],
      expirationDate: userExpirationDate?.expirationDate || '',
    });
  }),
});

export const fetchUsers = async () => await fetch(
  Config.endpoints.FETCH_USERS
)
  .then(handleErrors({ errorMessage: 'Fetching Users failed' }))
  .then(parseAsJson);

export const fetchUser = async (sso, { excludeRoleAssignments = YesNoFlag.N } = {}) => await fetch(
  Config.endpoints.FETCH_USER_SETTINGS
    .replace(/:sso/, sso)
    .replace(/:excludeRoleAssignments/, excludeRoleAssignments)
)
  .then(handleErrors({ errorMessage: 'Fetching User Settings failed' }))
  .then(parseAsJson);

export const fetchUserSettingsFormData = async periodName => await fetch(
  Config.endpoints.FETCH_USER_SETTINGS_FORM_DATA
    .replace(/:periodName/, periodName)
)
  .then(handleErrors({ errorMessage: 'Fetching User Settings Form Data failed' }))
  .then(parseAsJson);

export const addUserSettings = async ({ userSettingsData }) => await fetch(
  Config.endpoints.ADD_USER_SETTINGS,
  {
    method: 'POST',
    body: JSON.stringify(mapUserForApi(userSettingsData)),
  }
)
  .then(handleErrors({ errorMessage: 'Adding User Settings failed', parseResponseErrorMessage: true }));

export const updateUserSettings = async ({ userSettingsData }) => await fetch(
  Config.endpoints.UPDATE_USER_SETTINGS.replace(/:sso/, userSettingsData.sso),
  {
    method: 'PUT',
    body: JSON.stringify(mapUserForApi(userSettingsData)),
  }
)
  .then(handleErrors({ errorMessage: 'Updating User Settings failed', parseResponseErrorMessage: true }));

export const saveLastClientId = async ({ sso, clientId }) => await fetch(
  Config.endpoints.SET_USER_LAST_ID,
  {
    method: 'PUT',
    body: JSON.stringify({ clientId, sso }),
  }
)
  .then(handleErrors({ errorMessage: 'Saving user\'s last client id failed' }));


export const checkIfUserEmailExists = async ({ email }) => await fetch(
  Config.endpoints.CHECK_IF_USER_EMAIL_EXISTS,
  {
    method: 'POST',
    body: JSON.stringify({ email }),
  }
)
  .then(handleErrors({ errorMessage: 'Email validation failed' }))
  .then(parseAsJson);

export const checkIfUserPwcEmailSetup = async ({ email }) => await fetch(
  Config.endpoints.CHECK_IF_USER_PWC_EMAIL_SETUP,
  {
    method: 'POST',
    body: JSON.stringify({ email }),
  }
)
  .then(handleErrors({ errorMessage: 'Email verification failed' }))
  .then(parseAsJson);

export const checkIfUserEmailIsUnderDomain = async ({ email }) => await fetch(
  Config.endpoints.CHECK_IF_USER_EMAIL_IS_UNDER_DOMAIN,
  {
    method: 'POST',
    body: JSON.stringify({ email }),
  }
)
  .then(handleErrors({ errorMessage: 'Email domain verification failed' }))
  .then(parseAsJson);

export const checkIfUserSSOExists = async ({ sso }) => await fetch(
  Config.endpoints.CHECK_IF_USER_SSO_EXISTS,
  {
    method: 'POST',
    body: JSON.stringify({ sso }),
  }
)
  .then(handleErrors({ errorMessage: 'SSO validation failed' }))
  .then(parseAsJson);


export const fetchRoleAssignments = async ({ roleId }) => await fetch(
  Config.endpoints.FETCH_ROLE_ASSIGNMENTS
    .replace(/:roleId/, roleId)
)
  .then(handleErrors({ errorMessage: 'Role assignments fetching failed' }))
  .then(parseAsJson);

export const fetchUserPrivileges = async userPrivilege => await fetch(
  Config.endpoints.FETCH_ROLE_USER_PRIVILEGES
    .replace(/:userPrivilege/, userPrivilege)
)
  .then(handleErrors({ errorMessage: 'Fetching user privileges failed.' }))
  .then(parseAsJson);
